import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  ArrowLeft, Calendar, CheckCircle, Clock, Edit2, Send, Trash2,
  Download, ExternalLink, Filter, Check, X, Tag, MapPin
} from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8787/api';

interface SimplePlan {
  id: number;
  client_id: number;
  name: string;
  description?: string;
  type: 'weekly' | 'monthly' | 'quarterly';
  start_date: string;
  end_date: string;
  status: 'draft' | 'active' | 'completed' | 'archived';
  total_posts: number;
  published_posts: number;
  client?: {
    id: number;
    name: string;
  };
}

interface Post {
  id: number;
  post_type_name: string;
  post_type_category?: string;
  title: string;
  description?: string;
  scheduled_date: string;
  status: 'draft' | 'scheduled' | 'published' | 'cancelled';
  platform?: string;
  notes?: string;
  order_index: number;
  channels?: string; // JSON array of distribution channels
  assets?: string; // JSON array: page_content, infographic, images, video, podcast
  approved_by_client?: boolean;
  client_feedback?: string;
}

export default function SimplePlanView() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [plan, setPlan] = useState<SimplePlan | null>(null);
  const [posts, setPosts] = useState<Post[]>([]);
  const [filteredPosts, setFilteredPosts] = useState<Post[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState<string>('all');
  const [stats, setStats] = useState<any>(null);

  useEffect(() => {
    if (id) {
      loadPlan();
      loadStats();
    }
  }, [id]);

  useEffect(() => {
    filterPosts();
  }, [posts, selectedStatus]);

  const loadPlan = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/simple-plans/${id}`);
      setPlan(response.data);
      setPosts(response.data.posts || []);
    } catch (error) {
      console.error('Failed to load plan:', error);
      alert('Failed to load plan');
      navigate('/dashboard/plans');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/simple-plans/${id}/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const filterPosts = () => {
    if (selectedStatus === 'all') {
      setFilteredPosts(posts);
    } else {
      setFilteredPosts(posts.filter(p => p.status === selectedStatus));
    }
  };

  const updatePostStatus = async (postId: number, newStatus: 'draft' | 'scheduled' | 'published' | 'cancelled') => {
    try {
      await axios.put(`${API_BASE_URL}/simple-plans/${id}/posts/${postId}`, {
        status: newStatus
      });
      await loadPlan();
      await loadStats();
    } catch (error) {
      console.error('Failed to update post status:', error);
      alert('Failed to update post status');
    }
  };

  const deletePost = async (postId: number) => {
    if (!confirm('Are you sure you want to delete this post?')) return;

    try {
      await axios.delete(`${API_BASE_URL}/simple-plans/${id}/posts/${postId}`);
      await loadPlan();
      await loadStats();
    } catch (error) {
      console.error('Failed to delete post:', error);
      alert('Failed to delete post');
    }
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-700',
      scheduled: 'bg-blue-100 text-blue-700',
      published: 'bg-green-100 text-green-700',
      cancelled: 'bg-red-100 text-red-700'
    };
    return colors[status] || 'bg-gray-100 text-gray-700';
  };

  const getPlatformBadge = (platform?: string) => {
    if (!platform || platform === 'all') return null;

    const colors: Record<string, string> = {
      facebook: 'bg-blue-100 text-blue-700',
      instagram: 'bg-pink-100 text-pink-700',
      linkedin: 'bg-indigo-100 text-indigo-700',
      twitter: 'bg-sky-100 text-sky-700'
    };

    return (
      <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${colors[platform] || 'bg-gray-100 text-gray-700'}`}>
        {platform.charAt(0).toUpperCase() + platform.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Loading plan...</div>
      </div>
    );
  }

  if (!plan) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Plan not found</div>
      </div>
    );
  }

  const progress = plan.total_posts > 0 ? (plan.published_posts / plan.total_posts) * 100 : 0;

  return (
    <div className="p-6 lg:p-8">
      {/* Simplified Header */}
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{plan.name}</h1>
          <div className="flex items-center gap-3 mt-2 text-sm text-gray-600">
            <span>{formatDate(plan.start_date)} - {formatDate(plan.end_date)}</span>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(plan.status)}`}>
              {plan.status.charAt(0).toUpperCase() + plan.status.slice(1)}
            </span>
          </div>
        </div>
        <button
          onClick={() => navigate(`/dashboard/plans/simple/${id}/edit`)}
          className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
        >
          Edit Plan
        </button>
      </div>

      {/* Compact Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Total</p>
          <p className="text-2xl font-bold text-gray-900">{plan.total_posts}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Published</p>
          <p className="text-2xl font-bold text-green-600">{plan.published_posts}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Scheduled</p>
          <p className="text-2xl font-bold text-blue-600">{stats?.summary?.scheduled_posts || 0}</p>
        </div>
        <div className="bg-white rounded-lg border border-gray-200 p-4">
          <p className="text-xs text-gray-500">Progress</p>
          <p className="text-2xl font-bold text-indigo-600">{Math.round(progress)}%</p>
        </div>
      </div>

      {/* Post Types Breakdown - Simplified */}
      {stats?.by_type && stats.by_type.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <h3 className="text-sm font-semibold text-gray-900 mb-3">Content by Post Type</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
            {stats.by_type.map((item: any) => (
              <div key={item.post_type_name} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <span className="text-sm text-gray-700">{item.post_type_name}</span>
                <span className="text-sm font-medium text-gray-900">{item.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Status Filter */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
        <div className="flex items-center gap-2 overflow-x-auto">
          <Filter className="w-5 h-5 text-gray-400" />
          <button
            onClick={() => setSelectedStatus('all')}
            className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap ${
              selectedStatus === 'all'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            All ({posts.length})
          </button>
          <button
            onClick={() => setSelectedStatus('draft')}
            className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap ${
              selectedStatus === 'draft'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Draft ({stats?.summary?.draft_posts || 0})
          </button>
          <button
            onClick={() => setSelectedStatus('scheduled')}
            className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap ${
              selectedStatus === 'scheduled'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Scheduled ({stats?.summary?.scheduled_posts || 0})
          </button>
          <button
            onClick={() => setSelectedStatus('published')}
            className={`px-4 py-2 text-sm font-medium rounded-lg whitespace-nowrap ${
              selectedStatus === 'published'
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
            }`}
          >
            Published ({stats?.summary?.published_posts || 0})
          </button>
        </div>
      </div>

      {/* Posts Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Content
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Assets
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Channels
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Date
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredPosts.map(post => {
                const channels = post.channels ? JSON.parse(post.channels) : ['website'];
                const assets = post.assets ? JSON.parse(post.assets) : ['page_content'];
                return (
                <tr key={post.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="flex items-start gap-2">
                      {post.approved_by_client && (
                        <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                      )}
                      <div>
                        <div className="text-sm font-medium text-gray-900">{post.title}</div>
                        {post.description && (
                          <div className="text-xs text-gray-500 mt-0.5 line-clamp-1">{post.description}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded">
                      {post.post_type_name}
                    </span>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-wrap gap-1">
                      {assets.map((asset: string) => (
                        <span
                          key={asset}
                          className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${
                            asset === 'page_content' ? 'bg-blue-100 text-blue-700' :
                            asset === 'infographic' ? 'bg-purple-100 text-purple-700' :
                            asset === 'images' ? 'bg-pink-100 text-pink-700' :
                            asset === 'video' ? 'bg-red-100 text-red-700' :
                            asset === 'podcast' ? 'bg-green-100 text-green-700' :
                            'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {asset === 'page_content' ? 'Page' : asset.charAt(0).toUpperCase() + asset.slice(1)}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="flex flex-wrap gap-1">
                      {channels.map((channel: string) => (
                        <span
                          key={channel}
                          className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded ${
                            channel === 'website' ? 'bg-blue-100 text-blue-700' :
                            channel === 'google_business' ? 'bg-red-100 text-red-700' :
                            channel === 'social' ? 'bg-pink-100 text-pink-700' :
                            channel === 'email' ? 'bg-green-100 text-green-700' :
                            channel === 'youtube' ? 'bg-red-100 text-red-700' :
                            'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {channel.replace('_', ' ')}
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(post.status)}`}>
                      {post.status.charAt(0).toUpperCase() + post.status.slice(1)}
                    </span>
                  </td>
                  <td className="px-4 py-4 whitespace-nowrap">
                    <span className="text-sm text-gray-600">{formatDate(post.scheduled_date)}</span>
                  </td>
                </tr>
              );
              })}
            </tbody>
          </table>
        </div>

        {filteredPosts.length === 0 && (
          <div className="p-12 text-center">
            <Calendar className="w-12 h-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">No posts found</p>
          </div>
        )}
      </div>
    </div>
  );
}